import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DATE_FORMAT, MAX_FILE_SIZE } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxNumberBoxComponent,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTagBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule,
} from 'devextreme-angular';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import {
    AuthService,
    AppointmentDto,
    AppointmentsClient,
    CreateAppointmentCommand,
    AppointmentStatus,
    AppointmentLoadType,
    PermissionType,
    AccessType,
    UploadedFileDto,
    UpdateAppointmentCommand,
    DataSources,
    AppointmentAction,
    ShuntAppointmentCommand,
    AssignToUnloadAppointmentCommand,
    CompleteAppointmentCommand,
    AppointmentSection,
    CheckOutAppointmentCommand,
} from '@services';
import EnumUtils from '../../../../../shared/enum-utils';
import notify from 'devextreme/ui/notify';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import { AppointmentStatusPipe } from 'src/app/pipes/appointment-status.pipe';
import { FormPopupComponent } from 'src/app/components/utils/form-popup/form-popup.component';
import { AssignToUnloadFormComponent } from './assign-to-unload-form/assign-to-unload-form.component';
import { CompleteFormComponent } from './complete-form/complete-form.component';
import { AppointmentToolbarComponent } from './components/appointment-toolbar/appointment-toolbar.component';
import { FileUploadComponent } from 'src/app/components';
import { AppointmentComplianceComponent } from './components/appointment-compliance/appointment-compliance.component';
import { GuardHouseComponent } from './components/guard-house/guard-house.component';
import { FileUtils } from 'src/app/shared/file-utils';
import { AppointmentDateTimeComponent } from './components/appointment-date-time/appointment-date-time.component';
import * as AspNetData from "devextreme-aspnet-data-nojquery";
import { NotesComponent, NoteType } from 'src/app/components/library/notes/notes.component';
import { PurchaseOrderItemsComponent } from '../../../purchase-orders/purchase-order/components/purchase-order-items/purchase-order-items.component';
import { ShuntMoveFormComponent } from 'src/app/pages/yard-management/shunt-management/shunt-move-form/shunt-move-form.component';
import { AppointmentLoadTypePipe } from 'src/app/pipes/appointment-load-type-status.pipe';


@Component({
    selector: 'app-appointment-details',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxNumberBoxComponent,
        DxValidatorModule,
        DxSelectBoxModule,
        DxTagBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTextAreaModule,
        DxCheckBoxModule,
        AppointmentStatusPipe,
        AppointmentToolbarComponent,
        FileUploadComponent,
        ShuntMoveFormComponent,
        AssignToUnloadFormComponent,
        CompleteFormComponent,
        FormPopupComponent,
        AppointmentComplianceComponent,
        GuardHouseComponent,
        AppointmentDateTimeComponent,
        RouterLink,
        NotesComponent,
        PurchaseOrderItemsComponent,
    ],
    providers: [AppointmentLoadTypePipe],
    templateUrl: './appointment-details.component.html',
    styles: [
        `
            .view-wrapper-scroll {
                height: calc(100vh - 250px);
            }
        `,
    ],
})
export class AppointmentDetailsComponent implements OnInit {
    @Input() appointmentNumber: string | null = null;
    @ViewChild('validationGroup', { static: true }) validationGroup: DxValidationGroupComponent;
    @ViewChild(ShuntMoveFormComponent, { static: false }) createShuntMoveForm: ShuntMoveFormComponent;
    @ViewChild(AssignToUnloadFormComponent, { static: false }) assignToUnloadForm: AssignToUnloadFormComponent;
    @ViewChild(CompleteFormComponent, { static: false }) completeForm: CompleteFormComponent;
    @ViewChild(GuardHouseComponent, { static: false }) guardHouse: GuardHouseComponent;
    @ViewChild(DxScrollViewComponent, { static: false }) scrollView?: DxScrollViewComponent;
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    private route = inject(ActivatedRoute);
    private location = inject(Location);
    private router = inject(Router);
    protected auth = inject(AuthService);
    protected dataSources = inject(DataSources);    
    private appointmentLoadTypePipe = inject(AppointmentLoadTypePipe);
    private appointmentsClient = inject(AppointmentsClient);
    protected readonly NoteType = NoteType;
    public dateFormat = DATE_FORMAT;
    public AppointmentSection = AppointmentSection;

    protected supplierDataSource = this.dataSources.supplierOptions();
    protected carrierDataSource = this.dataSources.carrierOptions();
    public purchaseOrderDataSource: AspNetData.CustomStore | [] = [];

    isLoading: boolean = false;
    isSubmitting: boolean = false;
    maxFileSize: number = MAX_FILE_SIZE;
    newPackingSlipFiles: File[] = [];
    newOtherFiles: File[] = [];
    packingSlipFilesToRemove: string[] = [];
    otherFilesToRemove: string[] = [];
    isAddShuntMovePopupOpened = false;
    isAssignToUnloadPopupOpened = false;
    isCompletePopupOpened = false;
    isComplianceVisible = false;
    isDetailsReadOnly = false;
    purchaseOrderItems: AspNetData.CustomStore  | [] = [];
    pendingNoteAction: AppointmentAction | null = null;
    currentAction: AppointmentAction | null = null;
    protected appointment: AppointmentDto = new AppointmentDto();

    loadTypeOptions = EnumUtils.getEnumOptions(AppointmentLoadType, (v) =>
        this.appointmentLoadTypePipe.transform(v),
    );

    actionsRequiringNote: AppointmentAction[] = [
        AppointmentAction.Decline,
        AppointmentAction.SendBack,
        AppointmentAction.NoShow,
        AppointmentAction.RequestTags,
        AppointmentAction.TagsAsnPgiConfirmed,
        AppointmentAction.Investigate,
        AppointmentAction.InvestigationCompleted,
        AppointmentAction.RefuseLoad,
        AppointmentAction.AcceptLoad,
        AppointmentAction.CancelMove,
    ];

    actionsSkippingRequiredFields: AppointmentAction[] = [
        AppointmentAction.SaveAsDraft,
        AppointmentAction.Submit,
        AppointmentAction.SendBack,
        AppointmentAction.Decline,
        AppointmentAction.Save,
    ];

    validateConditionallyRequired = (params: { value: unknown }): boolean => {
        if (!this.currentAction)
            return true;

        if (this.actionsSkippingRequiredFields.includes(this.currentAction) && !this.appointment.showGuardHouse)
            return true;

        if (Array.isArray(params.value))
            return params.value.length > 0;

        return params.value !== null && params.value !== undefined && params.value !== '';
    }

    validatePurchaseOrders = (params: { value: unknown }): boolean => {
        if (this.appointment.loadType === AppointmentLoadType.CHEP || this.appointment.loadType === AppointmentLoadType.Returns)
            return true;

        return this.validateConditionallyRequired(params);
    }

    ngOnInit(): void {
        this.appointmentNumber = this.route.snapshot.paramMap.get('number');
        if (this.appointmentNumber) {
            this.loadAppointment();
        } else {
            this.appointment = new AppointmentDto();
            this.appointment.palletCount = 0;
            this.appointment.sections = [];
            this.isDetailsReadOnly = !this.auth.checkPermission(PermissionType.AppointmentDetails, AccessType.Edit);
        }
    }

    private loadAppointment() {
        this.isLoading = true;
        this.appointmentsClient
            .getAppointment(this.appointmentNumber!)
            .subscribe({
                next: async (data) => {
                    this.appointment = data;
                    if (this.appointment.supplierId) {
                        this.purchaseOrderDataSource = this.dataSources.purchaseOrderOptions(this.appointment.supplierId, data.purchaseOrders);
                        this.onPurchaseOrderChanged({ value: this.appointment.purchaseOrders });
                    }
                    this.isLoading = false;
                    this.isComplianceVisible = this.appointment.status === AppointmentStatus.Completed;
                    this.newPackingSlipFiles = [];
                    this.newOtherFiles = [];
                    this.packingSlipFilesToRemove = [];
                    this.otherFilesToRemove = [];
                    this.isDetailsReadOnly = !(data.sections?.includes(AppointmentSection.Details) ?? true);
                },
                error: () => {
                    notify('Failed to load appointment', 'error', 3000);
                    this.isLoading = false;
                    this.location.back();
                },
            });
    }
    
    get showDetails(): boolean {
        return this.auth.checkPermission(PermissionType.AppointmentDetails, AccessType.ViewOnly);
    }

    get showGuardHouse(): boolean {
        return (this.appointment?.showGuardHouse ?? false) &&
            this.auth.checkPermission(PermissionType.AppointmentGuardHouse, AccessType.ViewOnly);
    }

    get showCompliance(): boolean {
        return this.isComplianceVisible && this.auth.checkPermission(PermissionType.AppointmentCompliance, AccessType.ViewOnly);
    }

    get confirmedDateIsReadOnly(): boolean {
        return !(!this.isDetailsReadOnly && this.appointment.status === AppointmentStatus.UnderReview) ||
            !this.auth.checkPermission(PermissionType.AppointmentApprove, AccessType.Manage);
    }

    get guardHouseIsReadOnly(): boolean {
        return !this.appointment.sections!.includes(AppointmentSection.GuardHouse);
    }

    get canEditCompliance(): boolean {
        return this.appointment.sections!.includes(AppointmentSection.Compliance);
    }

    get toolbarIsReadOnly(): boolean {
        return this.isDetailsReadOnly && this.guardHouseIsReadOnly;
    }

    protected getBadgeClass(status: AppointmentStatus): string {
        return EnumUtils.getAppointmentStatusBadgeClass(status);
    }

    onSupplierChanged(e: any) {
        if (e.value) {
            this.purchaseOrderDataSource = this.dataSources.purchaseOrderOptions(e.value);
        } else {
            this.purchaseOrderDataSource = [];
        }

        this.appointment.purchaseOrders = [];
    }

    onPurchaseOrderChanged(e: any) {
        if (e.value) {
            this.purchaseOrderItems = this.dataSources.purchaseOrderItems(e.value);
        } else {
            this.purchaseOrderItems = [];
        }
    }

    addShuntMove() {
        this.isAddShuntMovePopupOpened = true;
    }

    assignToUnload() {
        this.isAssignToUnloadPopupOpened = true;
    }

    complete() {
        this.isCompletePopupOpened = true;
    }

    onShuntMovePopupSaveClick() {
        this.closePopupAndSave(AppointmentAction.ShuntMovePending, () => {
            this.isAddShuntMovePopupOpened = false;
        });
    }

    onAssignToUnloadPopupSaveClick() {
        this.closePopupAndSave(AppointmentAction.AssignToUnload, () => {
            this.isAssignToUnloadPopupOpened = false;
        });
    }

    onCompletePopupSaveClick() {
        this.closePopupAndSave(AppointmentAction.Complete, () => {
            this.isCompletePopupOpened = false;
        });
    }

    private closePopupAndSave(
        action: AppointmentAction,
        closePopup: () => void,
    ) {
        closePopup();
        this.save(action);
    }

    updateGuardHouseValidation(action: AppointmentAction) {
        if (this.guardHouse !== undefined) {
            this.guardHouse.isValidationEnabled = !(
                action === AppointmentAction.SendBack ||
                action === AppointmentAction.NoShow ||
                action === AppointmentAction.Decline
            );
        }
    }

    async onSaveClick(action: AppointmentAction) {     
        if(this.actionsRequiringNote.includes(action))
        {
            this.pendingNoteAction = action;
            this.notesComponent.openCreatePopup();

        } else{
            this.save(action);
        }
    }

    onNoteSaved() {
        if (!this.pendingNoteAction)
        {
            return;
        }

        const action = this.pendingNoteAction;
        this.pendingNoteAction = null;
        this.save(action);
    }

    onNoteCanceled() {
        this.pendingNoteAction = null;
    }

    async save(action: AppointmentAction) {
        this.currentAction = action;
        this.updateGuardHouseValidation(action);

        const result = this.validationGroup.instance.validate();

        if (!result.isValid) {
            notify(
                {
                    message: 'Please fix validation errors',
                    position: 'bottom center',
                },
                'error',
                3000,
            );
            return;
        }

        this.isSubmitting = true;

        const packingSlipFiles = await FileUtils.mapFilesToDto(
            this.newPackingSlipFiles,
        );
        const otherFiles = await FileUtils.mapFilesToDto(this.newOtherFiles);

        const request$ = this.buildRequest$(
            action,
            packingSlipFiles,
            otherFiles,
        );

        request$.subscribe({
            next: async (number) => {
                notify(
                    this.appointmentNumber
                        ? 'Appointment updated successfully'
                        : 'Appointment created successfully',
                    'success',
                    3000,
                );
                this.isSubmitting = false;
                if (action === AppointmentAction.Complete) {
                    await this.router.navigate(
                        ['/inbound/empty-check-ins/details', number],
                        {
                            replaceUrl: true,
                        },
                    );
                } else if (action === AppointmentAction.CheckOut) {
                    await this.router.navigate(
                        ['/outbound/empty-check-outs/details', number],
                        {
                            replaceUrl: true,
                        },
                    );
                } else if (this.appointmentNumber) {
                    this.loadAppointment();
                } else {
                    if(this.notesComponent)
                    {
                        await this.notesComponent.saveQueuedNotes(number);
                    }

                    await this.router.navigate(
                        ['/inbound/appointments/details', number],
                        {
                            replaceUrl: true,
                        },
                    );
                }
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message ||
                    (this.appointmentNumber
                        ? 'Failed to update appointment'
                        : 'Failed to create appointment');
                notify(msg, 'error', 3000);
                this.isSubmitting = false;
            },
        });
    }

    private buildRequest$(
        action: AppointmentAction,
        packingSlipFiles: UploadedFileDto[],
        otherFiles: UploadedFileDto[],
    ) {
        const appointmentNumber = this.appointmentNumber;

        if (appointmentNumber === null) {
            const command = new CreateAppointmentCommand({
                ...this.appointment,
                packingSlipFiles,
                otherFiles,
                action,
            });
            return this.appointmentsClient.createAppointment(command);
        }

        switch (action) {
            case AppointmentAction.ShuntMovePending: {
                const shuntMoveData = this.createShuntMoveForm.getData();
                const command = new ShuntAppointmentCommand({
                    ...this.appointment,
                    location: shuntMoveData.location,
                    shuntLocationId: shuntMoveData.locationId,
                    priority: shuntMoveData.priority,
                    note: shuntMoveData.note,
                    number: appointmentNumber,
                    packingSlipFiles,
                    otherFiles,
                    packingSlipFilesToRemove: this.packingSlipFilesToRemove,
                    otherFilesToRemove: this.otherFilesToRemove,
                    action,
                });
                return this.appointmentsClient.shuntAppointment(command);
            }
            case AppointmentAction.AssignToUnload: {
                const assignToUnload = this.assignToUnloadForm.getData();
                const command = new AssignToUnloadAppointmentCommand({
                    ...assignToUnload,
                    ...this.appointment,
                    number: appointmentNumber,
                    packingSlipFiles,
                    otherFiles,
                    packingSlipFilesToRemove: this.packingSlipFilesToRemove,
                    otherFilesToRemove: this.otherFilesToRemove,
                    action,
                });
                return this.appointmentsClient.assignToUnloadAppointment(
                    command,
                );
            }
            case AppointmentAction.Complete: {
                const complete = this.completeForm.getData();
                const command = new CompleteAppointmentCommand({
                    ...complete,
                    ...this.appointment,
                    number: appointmentNumber,
                    packingSlipFiles,
                    otherFiles,
                    packingSlipFilesToRemove: this.packingSlipFilesToRemove,
                    otherFilesToRemove: this.otherFilesToRemove,
                    action,
                });
                return this.appointmentsClient.completeAppointment(command);
            }
            case AppointmentAction.CheckOut: {
                const command = new CheckOutAppointmentCommand({
                    ...this.appointment,
                    number: appointmentNumber,
                    packingSlipFiles,
                    otherFiles,
                    packingSlipFilesToRemove: this.packingSlipFilesToRemove,
                    otherFilesToRemove: this.otherFilesToRemove,
                    action,
                });
                return this.appointmentsClient.checkOutAppointment(command);
            }
            default: {
                const command = new UpdateAppointmentCommand({
                    ...this.appointment,
                    number: appointmentNumber,
                    packingSlipFiles,
                    otherFiles,
                    packingSlipFilesToRemove: this.packingSlipFilesToRemove,
                    otherFilesToRemove: this.otherFilesToRemove,
                    action,
                });
                return this.appointmentsClient.updateAppointment(command);
            }
        }
    }

    cancel() {
        this.location.back();
    }
}
